import React, { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import Card from "@mui/material/Card"

import MDBox from "components/MDBox"
import MDButton from "components/MDButton"
import MDTypography from "components/MDTypography"

import { useLoading } from "../../LoadingContext"
import { useConnectionError } from "../../ConnectionErrorContext"
import { getSensitivityHistory, SensitivityHistoryItem } from "../../../api/sensitivity_history"
import { sensitivityLabel } from "../../../constants/sensitivity"

type PeriodValue = string[] | null

// 表示する履歴の件数（新しい順）
const HISTORY_DISPLAY_LIMIT = 10

type Props = {
  medicalRecordId: string
  title?: string
  onClose: () => void
}

const formatDateTime = (timestamp?: number | null) => {
  if (!timestamp) return ""
  const date = new Date(timestamp * 1000)
  return date.toLocaleString("ja-JP")
}

const formatSource = (source?: string | null) => {
  switch (source) {
    case "medical_record":
      return "入居者個別"
    case "catv":
      return "サービス提供元"
    case "system":
      return "システム設定"
    default:
      return "未設定"
  }
}

const formatPeriod = (value?: PeriodValue) => {
  if (!value || value.length < 2 || !value[0] || !value[1]) {
    return "未設定"
  }
  return `${value[0]}〜${value[1]}`
}

// 操作（どの画面から変更したか）を利用者向けの言葉にする。未知の値はそのまま表示する
const OPERATION_LABELS: Record<string, string> = {
  sensitivity_setting_update: "感度設定画面で変更",
  medical_record_update_sensitivity: "入居者詳細で変更",
  medical_record_update: "入居者の編集で変更"
}

const formatOperation = (value?: string | null) => (value && OPERATION_LABELS[value]) || formatValue(value)

const formatValue = (value?: string | number | null) => {
  if (value === undefined || value === null || value === "") {
    return "未設定"
  }
  return String(value)
}

// 入居者個別の設定が無い（保存値が 0 または空）場合の表示。
// 0 と空はどちらも「個別設定なし」で動作も同じため、利用者向けの言葉で1つにそろえる。
const INHERIT_LABEL = "サービス提供元の設定に従う"

const isIndividual = (value?: number | string | null) => value !== undefined && value !== null && Number(value) === 1

const SensitivityHistoryModal: React.FC<Props> = ({ medicalRecordId, title, onClose }) => {
  const navigate = useNavigate()
  const { startLoading, stopLoading } = useLoading()
  const { showConnectionError } = useConnectionError()

  const [items, setItems] = useState<SensitivityHistoryItem[]>([])
  const [message, setMessage] = useState("")

  useEffect(() => {
    const loadHistory = async () => {
      try {
        startLoading()
        const response = await getSensitivityHistory(medicalRecordId)

        if (response.success === 1) {
          // API は新しい順で全件返すため、最新の HISTORY_DISPLAY_LIMIT 件だけ表示する
          setItems((response.items || []).slice(0, HISTORY_DISPLAY_LIMIT))
        } else if (response.success === 99) {
          navigate("/login")
        } else {
          setMessage(response.message || "感度変更履歴の取得に失敗しました")
        }
      } catch (error: any) {
        if (error.response && error.response.status === 401) {
          navigate("/login")
        } else {
          showConnectionError(true)
        }
      } finally {
        stopLoading()
      }
    }

    loadHistory()
  }, [medicalRecordId])

  const historyHeaderCellStyle: React.CSSProperties = {
    padding: "8px",
    borderBottom: "1px solid #ddd",
    position: "sticky",
    top: 0,
    backgroundColor: "#fff",
    zIndex: 2,
    userSelect: "none"
  }

  return (
    <div className="modal-bg-cover">
      <Card
        sx={{ width: "95%", maxWidth: "1100px", maxHeight: "calc(100vh - 100px)", marginTop: "50px", padding: "20px" }}
      >
        <button className="close-button" onClick={onClose}>
          ×
        </button>
        <MDTypography variant="h4" fontWeight="medium">
          {title || "感度変更履歴"}
        </MDTypography>

        {message && (
          <MDBox mt={2} color="#d32f2f" fontSize="0.85rem">
            {message}
          </MDBox>
        )}

        <MDTypography variant="caption" color="text">
          ※最新の{HISTORY_DISPLAY_LIMIT}件を表示しています（各項目は変更後の設定）
        </MDTypography>
        <MDBox mt={1} sx={{ overflowX: "auto", overflowY: "auto", maxHeight: "calc(100vh - 240px)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
            <thead>
              <tr>
                <th style={historyHeaderCellStyle}>日時</th>
                <th style={historyHeaderCellStyle}>操作</th>
                <th style={historyHeaderCellStyle}>感度</th>
                <th style={historyHeaderCellStyle}>検知時間帯</th>
                <th style={historyHeaderCellStyle}>検知時間帯（オプション）</th>
                <th style={historyHeaderCellStyle}>適用元</th>
                {/* <th style={historyHeaderCellStyle}>操作ユーザー</th> */}
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td style={{ padding: "8px", borderBottom: "1px solid #eee" }}>{formatDateTime(item.create_date)}</td>
                  <td style={{ padding: "8px", borderBottom: "1px solid #eee", whiteSpace: "nowrap" }}>{formatOperation(item.operation_type)}</td>
                  <td style={{ padding: "8px", borderBottom: "1px solid #eee", whiteSpace: "nowrap" }}>
                    {isIndividual(item.after_sensitivity_enabled) ? `有効 / ${sensitivityLabel(item.after_sensitivity)}` : INHERIT_LABEL}
                  </td>
                  <td style={{ padding: "8px", borderBottom: "1px solid #eee", whiteSpace: "nowrap" }}>
                    {isIndividual(item.after_detection_enabled) ? `有効 / ${formatPeriod(item.after_detection_period)}` : INHERIT_LABEL}
                  </td>
                  <td style={{ padding: "8px", borderBottom: "1px solid #eee", whiteSpace: "nowrap" }}>
                    {isIndividual(item.after_option_enabled) ? `有効 / ${formatPeriod(item.after_option_period)}` : INHERIT_LABEL}
                  </td>
                  <td style={{ padding: "8px", borderBottom: "1px solid #eee", whiteSpace: "nowrap" }}>
                    {formatSource(item.setting_source_after)}
                  </td>
                  {/*<td style={{ padding: "8px", borderBottom: "1px solid #eee" }}>{formatValue(item.user_id)}</td>*/}
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ padding: "16px", textAlign: "center" }}>
                    感度変更履歴はありません。
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </MDBox>

        <MDBox mt={3} display="flex" justifyContent="flex-end">
          <MDButton variant="outlined" color="secondary" onClick={onClose}>
            閉じる
          </MDButton>
        </MDBox>
      </Card>
    </div>
  )
}

export default SensitivityHistoryModal
